import { useState } from 'react';
import {
  Receipt,
  CreditCard,
  ShoppingCart,
  Store,
  Lock,
  ShieldCheck,
  AlertTriangle,
  Building2,
  Copy,
  Check,
  ChevronRight,
  Tag,
  Home,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { useModules, useToggleModule } from '@/hooks/use-modules';
import { useOnboarding } from '@/hooks/use-onboarding';
import { useAuthStore } from '@/stores/auth-store';
import { useLocationStore } from '@/stores/location-store';
import { useLocationsWithExternalId } from '@/hooks/use-locations';
import { useHomePreference, useSetHomePreference } from '@/hooks/use-home-preference';
import { toast } from '@/stores/toast-store';
import { availableHomePages, type HomePageId } from './home/home-registry';
import { usePermissions } from '@/hooks/use-permissions';
import { cn, formatDateTime } from '@/lib/utils';

const MODULE_INFO: Record<string, { name: string; description: string; icon: React.ElementType }> = {
  collections: {
    name: 'Receive Payments',
    description: 'Manage invoices and collect payments from customers',
    icon: Receipt,
  },
  payables: {
    name: 'Send Payments',
    description: 'Manage vendor payments and bills',
    icon: CreditCard,
  },
  products: {
    name: 'POS Management',
    description: 'Manage point-of-sale products, departments, and inventory',
    icon: ShoppingCart,
  },
  esl: {
    name: 'Shelf Labels',
    description: 'Digital price labels on your shelves, updated automatically',
    icon: Tag,
  },
};

const ROLE_LEVEL: Record<string, number> = {
  owner: 4,
  admin: 3,
  manager: 2,
  employee: 1,
};

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
      className="ml-2 inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

export function SettingsPage() {
  const { selectedLocationId } = useLocationStore();
  const { data: modulesData, isLoading } = useModules(selectedLocationId);
  const toggleModule = useToggleModule();
  const { data: onboardingRes } = useOnboarding(selectedLocationId);
  const { user, tenant } = useAuthStore();
  const permissions = usePermissions();
  const { data: locationsData } = useLocationsWithExternalId();
  const modules = modulesData?.data ?? [];
  const userLevel = ROLE_LEVEL[user?.role || 'employee'] || 1;
  const canToggle = userLevel >= ROLE_LEVEL.admin;
  const isOwner = userLevel >= ROLE_LEVEL.owner;

  // Online Store lives here (not the sidebar): super-admin gates it per location
  // via the `online_store` module, and only then can the owner open its config.
  const onlineStoreMod = modules.find((m) => m.moduleName === 'online_store');
  const onlineStoreGateOpen = onlineStoreMod?.superAdminEnabled ?? false;

  // Default home page: offered per module active at this location (and the
  // role), saved per user. See home-registry.ts for the catalogue.
  const homeOptions = availableHomePages(modules, user?.role, permissions.hasAny);
  const { data: homePrefData } = useHomePreference();
  const setHomePreference = useSetHomePreference();
  const savedHome = homePrefData?.data?.defaultHomePage ?? null;
  const effectiveHome = homeOptions.find((p) => p.id === savedHome)?.id ?? homeOptions[0]?.id ?? null;

  function chooseHome(id: HomePageId | null) {
    if (id === savedHome) return;
    setHomePreference.mutate(id, {
      onSuccess: () => toast.success(id ? 'Default home page saved' : 'Default home page cleared'),
      onError: (err: any) => toast.error(err?.error?.message || 'Could not save home page'),
    });
  }

  const locations: any[] = locationsData?.data ?? [];
  const selectedLocation = locations.find((l) => l.id === selectedLocationId);
  const isLocationAlreadyActive = selectedLocation?.isActive ?? false;

  // Confirmation dialog state for deactivating a module
  const [deactivateTarget, setDeactivateTarget] = useState<string | null>(null);
  const deactivateModuleName = deactivateTarget ? MODULE_INFO[deactivateTarget]?.name : '';

  function handleToggle(moduleName: string, currentlyActive: boolean, gateOpen: boolean) {
    // Gate enforcement: if the platform admin hasn't activated this module for
    // this location, business admins can't turn it on. The button is visually
    // disabled too — this is a defensive guard.
    if (!currentlyActive && !gateOpen) {
      return;
    }
    if (currentlyActive) {
      // Turning OFF — show confirmation first
      setDeactivateTarget(moduleName);
    } else {
      // Turning ON — enable the module, then check if onboarding is needed
      toggleModule.mutate(
        { moduleName, isActive: true, locationId: selectedLocationId },
        {
          onSuccess: () => {
            if (moduleName === 'products') {
              window.location.hash = '#/pos-management';
            } else {
              // If the location is already registered and activated, the module is
              // simply turned on — no need to go through the registration flow again.
              if (isLocationAlreadyActive) {
                return;
              }
              // For payment modules, check if onboarding is needed
              const onboardingData = onboardingRes?.data;
              if (!onboardingData?.isSubmitted && selectedLocationId) {
                window.location.hash = `#/register/${selectedLocationId}`;
              }
            }
          },
        },
      );
    }
  }

  function confirmDeactivate() {
    if (deactivateTarget) {
      const target = deactivateTarget;
      toggleModule.mutate(
        { moduleName: target, isActive: false, locationId: selectedLocationId },
        {
          onSuccess: () => {
            if (target === 'products') {
              window.location.hash = '#/';
            }
          },
        },
      );
      setDeactivateTarget(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" description="Configure your business modules and preferences">
        <LocationBadge />
      </PageHeader>

      {/* Business Info Card */}
      {tenant && (
        <div className="rounded-xl border border-border bg-white p-6">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Business Information</h2>
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">{tenant.name}</p>
                {selectedLocation && (
                  <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                    {selectedLocation.nickname ?? selectedLocation.name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Customer ID */}
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Customer ID</p>
              <div className="flex items-center">
                <span className="break-all font-mono text-sm font-semibold tracking-wider text-foreground">
                  {tenant.externalId ?? '—'}
                </span>
                {tenant.externalId && <CopyButton value={tenant.externalId} />}
              </div>
            </div>
            {/* MID */}
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Merchant ID (MID)
              </p>
              <div className="flex items-center">
                <span className="break-all font-mono text-sm font-semibold tracking-wider text-foreground">
                  {selectedLocation?.externalId ?? '—'}
                </span>
                {selectedLocation?.externalId && <CopyButton value={selectedLocation.externalId} />}
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="mb-4 flex items-center gap-3">
          <h2 className="text-lg font-semibold">Modules</h2>
          {!canToggle && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              <Lock className="h-3 w-3" />
              Only admins can toggle modules
            </span>
          )}
        </div>
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">Loading...</div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {/* Intersected with what the API actually offers this business.
                MODULE_INFO on its own is a hardcoded list that no filtering
                reaches, so a module the platform had enabled simply never
                appeared here — and one the business can never use would have
                been offered. The API already narrows by account kind and by the
                platform release switch; this renders what it says. */}
            {Object.entries(MODULE_INFO)
              .filter(([key]) => modules.some((m) => m.moduleName === key))
              .map(([key, info]) => {
                const mod = modules.find((m) => m.moduleName === key);
                const isActive = mod?.isActive || false;
                // Defaults to true when the modules array hasn't loaded yet OR the
                // module isn't gated, so we don't flash a locked state. The
                // backend is the source of truth.
                const gateOpen = mod?.superAdminEnabled ?? true;
                const Icon = info.icon;

                const lockedByGate = !gateOpen && !isActive;
                const toggleDisabled = toggleModule.isPending || !canToggle || lockedByGate;
                const toggleTitle = !canToggle
                  ? 'Only admins can toggle modules'
                  : lockedByGate
                    ? `${info.name} must be enabled by a platform administrator before you can turn it on.`
                    : undefined;

                return (
                  <div key={key} className="space-y-4 rounded-xl border border-border bg-white p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <h3 className="font-medium">{info.name}</h3>
                      </div>
                      <button
                        onClick={() => handleToggle(key, isActive, gateOpen)}
                        disabled={toggleDisabled}
                        title={toggleTitle}
                        className={cn(
                          'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                          isActive ? 'bg-primary' : 'bg-gray-200',
                          toggleDisabled && 'opacity-50 cursor-not-allowed',
                        )}
                      >
                        <span
                          className={cn(
                            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                            isActive ? 'translate-x-6' : 'translate-x-1',
                          )}
                        />
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground">{info.description}</p>
                    {lockedByGate && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          This module must be enabled by a platform administrator for this location before you can turn
                          it on. Contact support if you need access.
                        </span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      Status:{' '}
                      <span className={isActive ? 'text-green-600' : 'text-gray-500'}>
                        {isActive ? 'Active' : lockedByGate ? 'Locked' : 'Inactive'}
                      </span>
                      {mod?.activatedAt && <span className="ml-2">since {formatDateTime(mod.activatedAt)}</span>}
                    </div>
                  </div>
                );
              })}

            {/* Online Store — gated by the super admin. When the gate is closed
                the card is locked; when open, the owner opens its configuration
                (store URL, WooCommerce credentials, sync). */}
            {(() => {
              const canConfigure = onlineStoreGateOpen && isOwner;
              const configTitle = !onlineStoreGateOpen
                ? 'Online Store must be enabled by a platform administrator for this location.'
                : !isOwner
                  ? 'Only the owner can configure the online store.'
                  : undefined;
              return (
                <div
                  className={cn(
                    'space-y-4 rounded-xl border border-border bg-white p-6',
                    !onlineStoreGateOpen && 'opacity-75',
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                        <Store className="h-5 w-5 text-primary" />
                      </div>
                      <h3 className="font-medium">Online Store</h3>
                    </div>
                    <button
                      onClick={() => {
                        window.location.hash = '#/online-store';
                      }}
                      disabled={!canConfigure}
                      title={configTitle}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                        canConfigure
                          ? 'bg-primary/10 text-primary hover:bg-primary/20'
                          : 'cursor-not-allowed bg-muted text-muted-foreground opacity-50',
                      )}
                    >
                      Configure
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Sell this location's products through a connected WooCommerce store, with stock kept in sync.
                  </p>
                  {!onlineStoreGateOpen && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        This module must be enabled by a platform administrator for this location before you can
                        configure it. Contact support if you need access.
                      </span>
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    Status:{' '}
                    <span className={onlineStoreGateOpen ? 'text-green-600' : 'text-gray-500'}>
                      {onlineStoreGateOpen ? 'Enabled' : 'Locked'}
                    </span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Deactivation Confirmation Dialog */}
      {deactivateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeactivateTarget(null)} />
          {/* Dialog */}
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground">Deactivate {deactivateModuleName}?</h3>
                <p className="text-sm text-muted-foreground">
                  Turning off the <strong>{deactivateModuleName}</strong> module will disable access to its features for
                  all users. Your existing data will be preserved and available when you reactivate the module.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setDeactivateTarget(null)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeactivate}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Default home page — which module's overview Home opens on. */}
      <div>
        <h2 className="mb-4 text-lg font-semibold">Home Page</h2>
        <div className="rounded-xl border border-border bg-white p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Home className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-medium">Default Home Page</h3>
              <p className="text-sm text-muted-foreground">
                What you see first when you open Home. Only modules enabled for this location are offered; where nothing
                is chosen, Home opens on the first one available.
              </p>
            </div>
          </div>
          {isLoading ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : homeOptions.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">
              No module home pages are available at this location yet. Turn on a module above to choose one.
            </p>
          ) : (
            <div
              role="radiogroup"
              aria-label="Default home page"
              className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
            >
              {homeOptions.map((p) => {
                const Icon = p.icon;
                const selected = p.id === effectiveHome;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={setHomePreference.isPending}
                    onClick={() => chooseHome(p.id)}
                    className={cn(
                      'flex items-start gap-3 rounded-lg border p-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-70',
                      selected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted',
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                        selected ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary',
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{p.label}</p>
                      <p className="text-xs text-muted-foreground">{p.description}</p>
                      {selected && (
                        <p className="mt-1 text-xs font-medium text-primary">
                          {savedHome === p.id ? 'Your default' : 'Current default (not saved)'}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div>
        <h2 className="mb-4 text-lg font-semibold">Security</h2>
        <div className="space-y-4 rounded-xl border border-border bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium">Two-Factor Authentication</h3>
                <p className="text-sm text-muted-foreground">
                  {user?.twoFactorEnabled
                    ? 'Your account is protected with 2FA.'
                    : 'Add an extra layer of security to your account.'}
                </p>
              </div>
            </div>
            <a
              href="#/settings/2fa"
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {user?.twoFactorEnabled ? 'Manage' : 'Enable'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
